import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Download, ArrowRightLeft, X, CheckCircle2 } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

const input = "w-full bg-white border hairline px-3 py-2 text-sm focus:outline-none focus:border-[color:var(--brand-primary)]";
const STATUS_LABELS = { booked: "Booked", presented: "Presented", cash_collected: "Cash Collected" };
const STATUS_STYLE = {
  booked: "bg-neutral-200 text-neutral-600",
  presented: "bg-[#a05a00]/15 text-[#a05a00]",
  cash_collected: "bg-[#0B3D2E] text-white",
};
const money = (v, ccy = "AUD") => `${ccy === "AUD" ? "A$" : ""}${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${ccy !== "AUD" ? ` ${ccy}` : ""}`;

const fmt = (v, ccy) => `${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${ccy}`;

const Converter = ({ currencies }) => {
  const [rates, setRates] = useState(null);
  const [amount, setAmount] = useState("10000");
  const [to, setTo] = useState(() => localStorage.getItem("payout_ccy") || "ZAR");
  useEffect(() => { api.get("/commissions/rates").then(r => setRates(r.data)); }, []);
  const changeTo = (v) => { localStorage.setItem("payout_ccy", v); setTo(v); };
  const rate = to === "AUD" ? 1 : rates?.rates?.[to];
  const converted = rates && amount && rate ? Number(amount) * rate : null;
  return (
    <div className="border hairline bg-white p-4" data-testid="currency-converter">
      <div className="label-eyebrow mb-3 flex items-center gap-2"><ArrowRightLeft size={12} /> Payout converter · AUD → your currency</div>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm text-neutral-500">A$</span>
        <input type="number" className={`${input} w-32`} value={amount} onChange={e => setAmount(e.target.value)} data-testid="converter-amount" />
        <span className="text-neutral-400 text-sm">→</span>
        <span className="font-display text-xl" data-testid="converter-result">
          {converted === null || Number.isNaN(converted) ? "—" : fmt(converted, "")}
        </span>
        <select className={`${input} w-24`} value={to} onChange={e => changeTo(e.target.value)} data-testid="converter-to">
          {currencies.filter(c => c !== "AUD").map(c => <option key={c}>{c}</option>)}
        </select>
      </div>
      {rates && rate && <div className="font-mono text-xs text-neutral-400 mt-2">1 AUD = {rate.toFixed(4)} {to} · live rates, updated {(rates.updated_at || "").slice(0, 10)}</div>}
    </div>
  );
};

const PayoutEquiv = ({ amountAud, currencies }) => {
  const [rates, setRates] = useState(null);
  const [ccy, setCcy] = useState(() => localStorage.getItem("payout_ccy") || "ZAR");
  useEffect(() => { api.get("/commissions/rates").then(r => setRates(r.data)); }, []);
  const rate = rates?.rates?.[ccy];
  if (!rates) return null;
  return (
    <div className="text-xs text-neutral-500 mt-1 flex items-center gap-1.5" data-testid="payout-equivalent">
      <span>≈ <strong className="font-display text-sm">{rate ? fmt(amountAud * rate, ccy) : "—"}</strong> paid in</span>
      <select value={ccy} onChange={e => { localStorage.setItem("payout_ccy", e.target.value); setCcy(e.target.value); }}
              className="bg-transparent border hairline text-xs px-1 py-0.5 focus:outline-none" data-testid="payout-currency">
        {currencies.filter(c => c !== "AUD").map(c => <option key={c}>{c}</option>)}
      </select>
    </div>
  );
};

const EarningsChart = ({ refreshKey }) => {
  const [history, setHistory] = useState(null);
  useEffect(() => {
    api.get("/commissions/history").then(r => setHistory(r.data));
  }, [refreshKey]);
  if (!history) return null;
  const data = history.map(h => ({ ...h, label: h.month.slice(2).replace("-", "/") }));
  return (
    <div className="border hairline bg-white p-5" data-testid="earnings-chart">
      <div className="label-eyebrow mb-4">Earnings trend · last 12 months (AUD)</div>
      <div style={{ width: "100%", height: 220 }}>
        <ResponsiveContainer>
          <BarChart data={data} margin={{ top: 4, right: 8, left: 8, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#eee" vertical={false} />
            <XAxis dataKey="label" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
            <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false}
                   tickFormatter={v => `$${v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}`} width={44} />
            <Tooltip formatter={(v, name) => [money(v), name === "earned_aud" ? "Earned" : "Paid out"]}
                     labelFormatter={l => `Month ${l}`} cursor={{ fill: "rgba(0,0,0,0.04)" }} />
            <Bar dataKey="earned_aud" name="earned_aud" fill="#0B3D2E" radius={[2, 2, 0, 0]} maxBarSize={28} />
            <Bar dataKey="paid_aud" name="paid_aud" fill="#a05a00" radius={[2, 2, 0, 0]} maxBarSize={28} />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="flex gap-5 mt-2 text-xs text-neutral-500">
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 inline-block bg-[#0B3D2E]" /> Earned (cash collected)</span>
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 inline-block bg-[#a05a00]" /> Paid out by management</span>
      </div>
    </div>
  );
};

const DealForm = ({ initial, products, currencies, onClose, onSaved }) => {
  const editing = Boolean(initial?.id);
  const [f, setF] = useState({
    client_name: initial?.client_name || "",
    product_id: initial?.product_id || products[0]?.id || "",
    sale_value: initial?.sale_value ?? "",
    currency: initial?.currency || "AUD",
    status: initial?.status || "booked",
    notes: initial?.notes || "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF(v => ({ ...v, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...f, sale_value: Number(f.sale_value) };
      if (editing) await api.put(`/commissions/deals/${initial.id}`, payload);
      else await api.post("/commissions/deals", payload);
      toast.success(editing ? "Deal updated" : "Deal added");
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || err.message); }
    finally { setSaving(false); }
  };
  return (
    <form onSubmit={submit} className="border-2 border-[color:var(--brand-primary)] bg-white p-5 space-y-3" data-testid="deal-form">
      <div className="flex items-center justify-between">
        <div className="label-eyebrow text-[var(--brand-primary)]">{editing ? "Edit deal" : "Add client / deal"}</div>
        <button type="button" onClick={onClose} className="text-neutral-400 hover:text-black" data-testid="deal-form-close"><X size={15} /></button>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
        <input className={input} placeholder="Client name *" required value={f.client_name} onChange={set("client_name")} data-testid="deal-client" />
        <select className={input} value={f.product_id} onChange={set("product_id")} required data-testid="deal-product">
          {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <input type="number" step="0.01" min="0" className={input} placeholder="Sale value *" required value={f.sale_value} onChange={set("sale_value")} data-testid="deal-value" />
        <select className={input} value={f.currency} onChange={set("currency")} data-testid="deal-currency">
          {currencies.map(c => <option key={c}>{c}</option>)}
        </select>
        <select className={input} value={f.status} onChange={set("status")} data-testid="deal-status">
          {Object.entries(STATUS_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
      </div>
      <input className={input} placeholder="Notes (optional)" value={f.notes} onChange={set("notes")} data-testid="deal-notes" />
      <div className="flex gap-2">
        <button className="btn-primary text-sm" disabled={saving || !products.length} data-testid="deal-save">{saving ? "Saving…" : editing ? "Save changes" : "Add deal"}</button>
        <button type="button" className="btn-ghost text-sm" onClick={onClose}>Cancel</button>
      </div>
    </form>
  );
};

export default function Commissions() {
  const [config, setConfig] = useState(null);
  const [data, setData] = useState(null);
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [formDeal, setFormDeal] = useState(undefined); // undefined=closed, null=new, obj=edit
  const reqId = useRef(0);

  const load = () => {
    const id = ++reqId.current;
    api.get("/commissions/deals", { params: { month } }).then(r => { if (id === reqId.current) setData(r.data); });
  };
  useEffect(() => { api.get("/commissions/config").then(r => setConfig(r.data)); }, []);
  useEffect(() => { load(); }, [month]);

  const advance = async (deal, status) => {
    try {
      await api.put(`/commissions/deals/${deal.id}`, { status });
      toast.success(`Moved to ${STATUS_LABELS[status]}`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || err.message); }
  };

  const del = async (deal) => {
    if (!window.confirm(`Delete deal for "${deal.client_name}"?`)) return;
    await api.delete(`/commissions/deals/${deal.id}`);
    toast.success("Deleted");
    load();
  };

  const exportCsv = async () => {
    const r = await api.get("/commissions/export", { params: { month }, responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url; a.download = `commissions-${month}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  if (!config || !data) return <div className="text-sm text-neutral-500" data-testid="commissions-loading">Loading commissions…</div>;
  const products = config.products.filter(p => p.active !== false);
  const s = data.summary;

  return (
    <div className="space-y-8" data-testid="commissions-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-eyebrow mb-3">Earnings</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.02]">Commission Tracker</h1>
          <p className="text-neutral-500 mt-3">Your pipeline, your commissions — converted to AUD, export-ready for payroll.</p>
        </div>
        <div className="flex items-center gap-2">
          <input type="month" className={input} value={month} onChange={e => setMonth(e.target.value)} data-testid="commissions-month" />
          <button className="btn-ghost text-sm flex items-center gap-2" onClick={exportCsv} data-testid="commissions-export"><Download size={13} /> Export CSV</button>
        </div>
      </div>

      <div className="grid sm:grid-cols-3 gap-4">
        <div className="border hairline bg-white p-5" data-testid="stat-earned">
          <div className="font-display text-3xl text-[var(--brand-primary)]">{money(s.earned_aud)}</div>
          <div className="label-eyebrow mt-1">Earned in {s.month} (cash collected)</div>
          <div className="text-xs text-neutral-500 mt-2" data-testid="stat-paid-breakdown">
            Paid out: <strong>{money(s.paid_aud)}</strong> · Awaiting payment: <strong>{money(s.unpaid_aud)}</strong>
          </div>
          <PayoutEquiv amountAud={s.earned_aud} currencies={config.currencies} />
        </div>
        <div className="border hairline bg-white p-5" data-testid="stat-pipeline-commission">
          <div className="font-display text-3xl">{money(s.pipeline_commission_aud)}</div>
          <div className="label-eyebrow mt-1">Commission in pipeline</div>
        </div>
        <div className="border hairline bg-white p-5" data-testid="stat-pipeline-value">
          <div className="font-display text-3xl">{money(s.pipeline_value_aud)}</div>
          <div className="label-eyebrow mt-1">Pipeline sale value · {s.counts.booked} booked / {s.counts.presented} presented</div>
        </div>
      </div>

      <EarningsChart refreshKey={`${s.earned_aud}-${s.paid_aud}-${data.deals.length}`} />

      <div className="flex flex-wrap items-start justify-between gap-4">
        <button className="btn-primary text-sm flex items-center gap-2" onClick={() => setFormDeal(null)} data-testid="add-deal-btn">
          <Plus size={14} /> Add client / deal
        </button>
        <Converter currencies={config.currencies} />
      </div>

      {products.length === 0 && (
        <div className="border hairline bg-white p-4 text-sm text-neutral-500" data-testid="no-products-warning">
          No product lines configured yet — ask your manager to add them in Admin → Commissions.
        </div>
      )}

      {formDeal !== undefined && (
        <DealForm initial={formDeal} products={products} currencies={config.currencies}
                  onClose={() => setFormDeal(undefined)}
                  onSaved={() => { setFormDeal(undefined); load(); }} />
      )}

      <div className="border hairline bg-white overflow-x-auto">
        <table className="w-full text-sm" data-testid="deals-table">
          <thead className="bg-[color:var(--brand-surface2)]">
            <tr className="text-left label-eyebrow">
              <th className="p-3">Client</th><th className="p-3">Product line</th><th className="p-3">Sale</th>
              <th className="p-3">Status</th><th className="p-3">Commission</th><th className="p-3">Commission (AUD)</th>
              <th className="p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {data.deals.length === 0 && (
              <tr><td colSpan={7} className="p-6 text-center text-neutral-500" data-testid="deals-empty">
                No deals yet — add your first client above. Pipeline deals always show; cash-collected deals show for the selected month.
              </td></tr>
            )}
            {data.deals.map(d => (
              <tr key={d.id} className="border-t hairline" data-testid={`deal-row-${d.id}`}>
                <td className="p-3">
                  <div className="font-medium">{d.client_name}</div>
                  {d.notes && <div className="text-xs text-neutral-400">{d.notes}</div>}
                </td>
                <td className="p-3 text-neutral-600">{d.product_name}
                  <div className="text-xs text-neutral-400">{d.commission_type === "percent" ? `${d.commission_rate}%` : `A$${d.commission_rate} flat`}</div>
                </td>
                <td className="p-3 font-mono text-xs">{money(d.sale_value, d.currency)}</td>
                <td className="p-3">
                  <select value={d.status} onChange={e => advance(d, e.target.value)}
                          className={`text-[10px] uppercase tracking-widest px-2 py-1 border-0 cursor-pointer ${STATUS_STYLE[d.status]}`}
                          data-testid={`deal-status-${d.id}`}>
                    {Object.entries(STATUS_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                  </select>
                  {d.cash_collected_at && <div className="text-xs text-neutral-400 mt-1">{d.cash_collected_at.slice(0, 10)}</div>}
                </td>
                <td className="p-3 font-mono text-xs">{money(d.commission_native, d.commission_currency)}</td>
                <td className="p-3 font-display" data-testid={`deal-aud-${d.id}`}>
                  {money(d.commission_aud)}
                  {d.paid_out && (
                    <span className="ml-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-1.5 py-0.5 bg-[#0B3D2E] text-white align-middle" data-testid={`deal-paid-badge-${d.id}`}>
                      <CheckCircle2 size={9} /> Paid
                    </span>
                  )}
                </td>
                <td className="p-3">
                  <div className="flex gap-1">
                    <button className="btn-ghost text-xs px-2 py-1" onClick={() => setFormDeal(d)} data-testid={`deal-edit-${d.id}`}><Pencil size={11} /></button>
                    <button className="text-xs px-2 py-1 text-[#8B2E2E] hover:bg-[#8B2E2E]/10" onClick={() => del(d)} data-testid={`deal-delete-${d.id}`}><Trash2 size={11} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-neutral-400">Cash-collected commissions lock in the exchange rate on the day of collection. Pipeline amounts use today's live rate. Flat-$ product lines pay in AUD.</p>
    </div>
  );
}
